import { useFormatter } from '@api/useFormatter'
import dcIcon from '@assets/Logos/dc_icon.svg'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import { addCommasToNumber, BBDTag, Horizontal, Vertical, Texto } from '@gravitate-js/excalibrr'
import { Col, Row, Switch, Tooltip } from 'antd'
import classNames from 'classnames'
import React from 'react'

import { useSupplyOptionContext } from '../../../../Contexts/SupplyOptionContext'
import { MultipleOptionSummary } from './MultipleOptionSummary'
import { PriceWarning, SupplyOptionRow } from './SupplyOptionRow'
import { IconInfoCircleFilled } from '@tabler/icons-react'
import { useWizardMaster } from '@/components/OrderWizard/Contexts/WizardMasterContext'
import { paneModes } from '@/constants/orderWizard'

export function SupplyOptionCard({ supplyOption, selected, isBest, showLockIn = true }) {
  const { selectedStatus, selectedSupply, supplyOptionsPayload } = useSupplyOptionContext()
  const { wizardOrder, handleChangeSupplyLock, isComplete, paneMode } = useWizardMaster()
  const { scopes } = useSecurityContext()
  const isCarrier = scopes?.is_carrier
  if (!supplyOption) return null
  const showWarning = selectedStatus !== 'selected' && selectedStatus !== '' && selected
  const showLockSupply = !isComplete && paneMode === paneModes.TANKIMPACT
  const originalDestinations = (wizardOrder?.loads || []).map((load) => load.location_name)

  return (
    <Vertical flex='none' height='auto' style={{ overflow: 'visible' }} className='m-3'>
      {supplyOption.delivered && (
        <div className='delivered-option-tag'>
          <img src={dcIcon} alt='delivered' width={24} height={24} className='pr-2' />
          <Texto textTransform='uppercase' appearance='white'>
            Delivered Contract
          </Texto>
        </div>
      )}

      <Vertical
        flex={'none'}
        className={`supply-option-card ${selected && !isCarrier ? selectedStatus : ''} ${
          supplyOption?.delivered ? 'delivered' : ''
        }`}
        height={'auto'}
      >
        <Horizontal className={classNames({ 'medium-opacity': selected && selectedStatus === 'invalid' })}>
          <Vertical verticalCenter height={'auto'}>
            {supplyOption.prices.map((price, i) => {
              return (
                <SupplyOptionRow
                  key={i}
                  supplyOwnerMap={supplyOptionsPayload?.supply_owner_map || wizardOrder?.supply_option.supply_owner_map}
                  price={price}
                  isCarrier={isCarrier}
                  isDelivered={supplyOption.delivered}
                />
              )
            })}
          </Vertical>
          <MultipleOptionSummary
            supplyOption={supplyOption}
            selected={selected}
            isBest={isBest}
            originalDestinations={originalDestinations}
            showLockIn={showLockIn}
            isCarrier={isCarrier}
          />
        </Horizontal>
        {showLockSupply && (
          <Horizontal verticalCenter className='pl-4 pb-4 pt-2' gap={4}>
            <Texto>Lock Supply </Texto>
            <Tooltip title={'Prevents automatic supply changes'}>
              <IconInfoCircleFilled size={14} />
            </Tooltip>
            <Switch className='ml-2' checked={wizardOrder.supply_locked} onChange={handleChangeSupplyLock} />
          </Horizontal>
        )}
        {showWarning && <PriceWarning option={selectedSupply} isCarrier={isCarrier} />}
      </Vertical>
    </Vertical>
  )
}

export function TotalPriceDisplay({ supplyOption, showTotalPrice }) {
  const { formatCurrency } = useFormatter()
  const formattedCurrency = formatCurrency(null, true)
  if (showTotalPrice) {
    return (
      <Horizontal justifyContent='space-between' verticalCenter>
        <Texto style={{ color: 'var(--gray-500)' }} category='label'>
          TOTAL
        </Texto>
        <Texto style={{ whiteSpace: 'nowrap' }} category='h4' weight='bold'>
          {formattedCurrency}
          {supplyOption.landed_cost && addCommasToNumber(supplyOption.landed_cost, 2)}
        </Texto>
      </Horizontal>
    )
  }

  if (supplyOption.delta_to_best === 0) {
    return (
      <Row>
        <Col span={8}>
          <div className='detail-data-label'>Delta To Best</div>
        </Col>
        <Col span={16}>
          <div className='detail-data-value pt-2 text-right'>
            <div className='tag-container'>
              <BBDTag theme2 className='option-trophy-tag'>
                IS BEST
              </BBDTag>
            </div>
          </div>
        </Col>
      </Row>
    )
  }

  if (!supplyOption.delta_to_best) {
    return null
  }

  return (
    <Row>
      <Col span={8}>
        <div className='detail-data-label'>Delta To Best</div>
      </Col>
      <Col span={16}>
        <div className='detail-data-value pt-2 text-right'>
          {formattedCurrency}
          {supplyOption.delta_to_best && addCommasToNumber(supplyOption.delta_to_best.toFixed(2))}
        </div>
      </Col>
    </Row>
  )
}
